<template>
  <div class="fin-panel-body">
    <finance-state v-if="status === 'loading'" kind="loading" :lines="2" />

    <template v-else>
      <div v-if="status === 'error'" class="ui-banner ui-banner--info ">
        <v-icon size="16" color="currentColor">mdi-information-outline</v-icon><span>{{ $t("finance_shell.order.summary_unavailable") }}</span>
      </div>

      <dl v-if="summary" class="fin-kv">
        <dt>{{ $t("finance_customer") }}</dt><dd>{{ summary.customerName || "—" }}</dd>
        <template v-if="summary.customerPhone"><dt>{{ $t("finance_shell.order.phone") }}</dt><dd>{{ summary.customerPhone }}</dd></template>
        <dt>{{ $t("finance_payment_method_label") }}</dt><dd>{{ paymentText }}</dd>
        <dt>{{ $t("finance_line_value") }}</dt><dd class="fin-num">{{ money(summary.orderValue) }}</dd>
        <template v-if="summary.deliveryPrice !== null"><dt>{{ $t("finance_shell.order.delivery") }}</dt><dd class="fin-num">{{ money(summary.deliveryPrice) }}</dd></template>
        <template v-if="summary.alreadyRefunded > 0"><dt>{{ $t("finance_shell.order.already_refunded") }}</dt><dd class="fin-num">{{ money(summary.alreadyRefunded) }}</dd></template>
      </dl>

      <div v-if="group" class="fin-lines-box">
        <div v-for="line in group.lines" :key="line.id" class="fin-lines-box__row">
          <span>{{ lineLabel(line) }}</span>
          <b class="fin-num" :class="line.isCash ? 'is-cash' : line.signed < 0 ? 'is-out' : line.isRevenue ? '' : 'is-in'">{{ money(line.signed, !line.isRevenue) }}</b>
        </div>
        <div class="fin-lines-box__row fin-lines-box__row--total">
          <span>{{ $t("finance_net_for_you") }}</span>
          <b class="fin-num" :class="group.net < 0 ? 'is-out' : 'is-in'">{{ money(group.net, true) }}</b>
        </div>
      </div>

      <button type="button" class="ui-btn ui-btn--block" @click="detailOpen = true">
        <v-icon size="16">mdi-receipt-text-outline</v-icon>{{ $t("finance_order_details") }}
      </button>

      <h4 class="fin-h4">{{ $t("finance_request_refund") }}</h4>
      <p v-if="blocked" class="ui-muted">{{ $t("finance_shell.order.nothing_to_refund") }}</p>
      <form v-else class="fin-form" novalidate @submit.prevent="submit">
        <div class="ui-field">
          <label for="fin-ref-amount">{{ $t("finance_shell.order.refund_amount", { max: maxText }) }}</label>
          <input id="fin-ref-amount" v-model="amount" class="ui-input fin-num" inputmode="decimal" autocomplete="off" :aria-invalid="String(Boolean(amountError))" />
          <span v-if="amountError" class="ui-err" role="alert">{{ amountError }}</span>
        </div>
        <div class="ui-field">
          <label for="fin-ref-reason">{{ $t("finance_reason") }}</label>
          <input id="fin-ref-reason" v-model="reason" class="ui-input" autocomplete="off" :aria-invalid="String(tried && !reason.trim())" />
          <span v-if="tried && !reason.trim()" class="ui-err" role="alert">{{ $t("finance_required_field") }}</span>
        </div>
        <div class="ui-field">
          <label for="fin-ref-method">{{ $t("finance_shell.order.refund_method") }}</label>
          <select id="fin-ref-method" v-model="method" class="ui-input">
            <option value="">—</option>
            <option value="bank">{{ $t("finance_shell.method.bank") }}</option>
            <option value="cash">{{ $t("finance_shell.method.cash") }}</option>
          </select>
        </div>
        <button type="submit" class="ui-btn ui-btn--primary ui-btn--block" :disabled="saving">{{ $t("finance_shell.order.send_request") }}</button>
        <small class="ui-muted">{{ $t("finance_shell.order.pending_note") }}</small>
      </form>
    </template>

    <finance-order-detail-dialog v-model="detailOpen" :order-id="orderId" />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import { formatMoney } from "@/components/restaurant/finance/constants";
import FinanceOrderDetailDialog from "@/components/restaurant/finance/FinanceOrderDetailDialog.vue";
import FinanceState from "../parts/FinanceState.vue";
import hasFinanceLabels from "../hasFinanceLabels";
import { mapOrderSummary, parseAmount } from "../financeLogic";

/**
 * Narudžba iz finansija: sažetak (GET restaurants/{id}/orders/{orderId}/summary), kartica sa iznosima i zahtjev za
 * refundaciju. Iznos je unaprijed najveći dozvoljeni (max_refund) i ne može ga preći. Zahtjev ostaje na čekanju dok ga
 * administrator ne odobri. "Detalji narudžbe" otvara postojeći dijalog iz izvještaja.
 */
export default {
  name: "FinanceOrderPanel",
  components: { FinanceState, FinanceOrderDetailDialog },
  mixins: [hasFinanceLabels],
  inject: { shell: "reportShell" },
  props: {
    orderId: { type: Number, required: true },
    restaurantId: { type: [Number, String], default: null },
    currency: { type: String, default: "KM" },
    /** kartica transakcije (opciono): redovi i neto */
    group: { type: Object, default: null },
  },
  data: () => ({ status: "loading", summary: null, amount: "", reason: "", method: "", tried: false, saving: false, detailOpen: false }),
  computed: {
    max() {
      return this.summary && this.summary.maxRefund !== null ? this.summary.maxRefund : null;
    },
    maxText() {
      return this.max === null ? "—" : this.money(this.max);
    },
    blocked() {
      return this.max !== null && this.max <= 0;
    },
    paymentText() {
      const method = this.summary.paymentMethod;
      return method === "CARD" ? this.$t("finance_payment_card") : method === "CASH" ? this.$t("finance_payment_cash") : "—";
    },
    amountValue() {
      return parseAmount(this.amount);
    },
    amountError() {
      if (!this.tried && this.amount === "") {
        return "";
      }
      if (!(this.amountValue > 0)) {
        return this.$t("finance_amount_must_be_positive");
      }
      if (this.max !== null && this.amountValue > this.max + 0.001) {
        return this.$t("finance_amount_exceeds_max_refund", { amount: this.max });
      }
      return "";
    },
  },
  created() {
    this.load();
  },
  methods: {
    money(value, signed) {
      return formatMoney(value, this.currency, { signed });
    },
    async load() {
      if (!this.restaurantId) {
        this.status = "error";
        return;
      }
      this.status = "loading";
      try {
        const { data } = await restaurantFinanceService.getOrderSummary(this.restaurantId, this.orderId);
        this.summary = mapOrderSummary((data && data.data) || data);
        this.amount = this.max === null || this.max <= 0 ? "" : String(this.max).replace(".", ",");
        this.status = "ready";
      } catch (error) {
        // forma ostaje (bez gornje granice), backend je ipak provjerava
        this.summary = null;
        this.status = "error";
      }
    },
    async submit() {
      this.tried = true;
      if (this.saving || this.amountError || !this.reason.trim() || !(this.amountValue > 0)) {
        return;
      }
      this.saving = true;
      try {
        await restaurantFinanceService.createRefund(this.restaurantId, {
          order_id: this.orderId,
          customer: (this.summary && this.summary.customerName) || "",
          reason: this.reason.trim(),
          amount: this.amountValue,
          method: this.method || undefined,
        });
        this.shell.showToast(this.$t("finance_refund_requested"));
        this.shell.closePanel();
        this.shell.reload();
      } catch (error) {
        const response = error && error.response && error.response.data;
        this.shell.showToast((response && response.message) || this.$t("finance_refund_request_error"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
